import { useContext, useEffect, useState, useCallback } from 'react';
import { UserContext } from '@services/Session';
import axios from '@helpers/axios';
import * as Sentry from '@sentry/react';

import {
  isMonetizedSport,
  isPlayerEntitled,
  isLoggedInUserEntitled,
} from '@helpers/utils/monetizationUtils';

import { useIsFeatureEnabled } from '@helpers/flagUtils';

const useCanEditMonetizedPlayerFields = (
  playerId: string,
  selectedSport?: string
) => {
  const userStore = useContext(UserContext);
  const monetizationEnabled = useIsFeatureEnabled('vb_monetization', userStore);

  const [canEditMonetizedFields, setCanEditMonetizedFields] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkIsTeamManagerOfPlayer = useCallback(
    async (teamIds: string[]) => {
      try {
        const response = await axios.get(
          `/api/claimed_player/${playerId}/team_ids`
        );
        const playerTeamIds = response.data.teamIds;
        return playerTeamIds.some((tId: string) => teamIds.includes(tId));
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [playerId]
  );

  useEffect(() => {
    const checkEntitlement = async () => {
      if (!selectedSport || selectedSport !== 'bbsb') {
        setCanEditMonetizedFields(true);
        setLoading(false);
        return;
      }

      if (!isMonetizedSport(selectedSport, monetizationEnabled)) {
        setCanEditMonetizedFields(true);
        setLoading(false);
        return;
      }

      // return true if player is entitled OR the user is entitled OR the user is a team manager
      const isTeamManager = await checkIsTeamManagerOfPlayer(
        userStore.userTeams.map((t) => t._id)
      );

      if (isTeamManager) {
        setCanEditMonetizedFields(true);
        setLoading(false);
        return;
      }

      try {
        const userIsEntitled = await isLoggedInUserEntitled();
        if (userIsEntitled) {
          setCanEditMonetizedFields(true);
          setLoading(false);
          return;
        }

        const playerIsEntitled = await isPlayerEntitled(playerId);
        setCanEditMonetizedFields(playerIsEntitled);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'An error occurred');
        Sentry.captureException(e);
      } finally {
        setLoading(false);
      }
    };

    playerId && checkEntitlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, selectedSport]);

  return { canEditMonetizedFields, loading, error };
};

export default useCanEditMonetizedPlayerFields;
